import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Autocomplete,
} from '@mui/material';
import { Article } from '@mui/icons-material';
import {
  fetchSerpAwareness,
  fetchSerpAwarenessHistory,
  type SerpAwarenessResponse,
  type SerpAwarenessHistoryResponse,
} from '../../services/analytics.service';
import { usePagesList } from '../../hooks/usePagesList';

export default function SerpAnalyticsPage() {
  const { pages, loading: loadingPages } = usePagesList();
  const [tenantId, setTenantId] = useState<number | ''>(1);
  const [pageId, setPageId] = useState<number | ''>('');
  const [windowDays, setWindowDays] = useState<number>(28);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<SerpAwarenessResponse | null>(null);
  const [history, setHistory] = useState<SerpAwarenessHistoryResponse | null>(null);

  const loadSerpAwareness = async () => {
    if (pageId === '' || pageId === undefined) {
      setError('Page ID is required');
      return;
    }

    const tenant = tenantId === '' ? 1 : Number(tenantId);
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSerpAwareness(Number(pageId), {
        tenant_id: tenant,
        window: windowDays,
      });
      setSnapshot(res.data);

      const historyRes = await fetchSerpAwarenessHistory(Number(pageId), {
        tenant_id: tenant,
        window: windowDays,
      });
      setHistory(historyRes.data);
    } catch (e: any) {
      setSnapshot(null);
      setHistory(null);
      setError(e?.response?.data?.detail || e?.message || 'Failed to load SERP awareness');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSnapshot(null);
    setHistory(null);
  }, [pageId, tenantId, windowDays]);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">SERP</Typography>

      {/* Snapshot Filters */}
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Tenant ID"
              type="number"
              size="small"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ width: 140 }}
            />
            <Autocomplete
              size="small"
              sx={{ width: 280 }}
              options={pages}
              value={pages.find(p => p.id === pageId) || null}
              onChange={(_, newValue) => setPageId(newValue?.id || '')}
              getOptionLabel={(option) => option.label}
              loading={loadingPages}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Page"
                  placeholder="Select Page"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Article sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Article fontSize="small" color="action" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {option.url.length > 40 ? '...' + option.url.slice(-40) : option.url}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <TextField
              label="Window (days)"
              type="number"
              size="small"
              value={windowDays}
              onChange={(e) => setWindowDays(Math.max(1, Math.min(90, Number(e.target.value) || 28)))}
              sx={{ width: 150 }}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={loadSerpAwareness} disabled={loading}>
                Load
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setTenantId(1);
                  setPageId('');
                  setWindowDays(28);
                  setSnapshot(null);
                  setHistory(null);
                  setError(null);
                }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Snapshot and history */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Snapshots</Typography>
          <Divider sx={{ mb: 2 }} />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading…</Typography>
            </Stack>
          )}
          {!loading && !snapshot && !error && (
            <Typography>Enter a Page ID and click Load to fetch SERP awareness.</Typography>
          )}
          {!loading && snapshot && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Computed {new Date(snapshot.computed_at).toLocaleString()} • Engine {snapshot.engine?.toUpperCase()} • Window {snapshot.window_days} days
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Volatility ${snapshot.volatility_score.toFixed(1)}`} color={snapshot.is_volatile ? 'warning' : 'success'} />
                {snapshot.top3_percentage !== undefined && (
                  <Chip label={`Top 3: ${snapshot.top3_percentage.toFixed(1)}%`} />
                )}
                {snapshot.total_impressions !== undefined && (
                  <Chip label={`Impressions: ${snapshot.total_impressions.toLocaleString()}`} />
                )}
              </Stack>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Position Distribution</Typography>
                <Stack spacing={1}>
                  {Object.entries(snapshot.position_buckets || {}).map(([bucket, impressions]) => (
                    <Stack key={bucket} direction="row" spacing={1} alignItems="center">
                      <Chip label={`Positions ${bucket}`} size="small" />
                      <Typography variant="body2">{impressions.toLocaleString()} impressions</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Search Appearance</Typography>
                {snapshot.appearance_breakdown && Object.keys(snapshot.appearance_breakdown).length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {Object.entries(snapshot.appearance_breakdown).map(([type, count]) => (
                      <Chip key={type} label={`${type} (${count.toLocaleString()})`} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No appearance breakdown available.</Typography>
                )}
              </Box>
              {snapshot.insights && snapshot.insights.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Insights</Typography>
                  <List dense>
                    {snapshot.insights.map((insight, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={insight} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Stack>
          )}

          {!loading && history?.snapshots?.length ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>History</Typography>
              <List>
                {history.snapshots.map((item) => (
                  <ListItem key={item.id} divider>
                    <ListItemText
                      primary={`Computed ${new Date(item.computed_at).toLocaleString()}`}
                      secondary={`Volatility ${item.volatility_score.toFixed(1)} • Top3 ${item.top3_percentage?.toFixed?.(1) ?? '—'}% • Impressions ${item.total_impressions?.toLocaleString?.() ?? '—'}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* Appearance & insights */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Features & Context</Typography>
          {snapshot ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Search Appearance</Typography>
                {snapshot.appearance_breakdown && Object.keys(snapshot.appearance_breakdown).length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {Object.entries(snapshot.appearance_breakdown).map(([type, count]) => (
                      <Chip key={type} label={`${type} (${count.toLocaleString()})`} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No appearance data available.</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                {snapshot.notes ? (
                  <List dense>
                    {Object.entries(snapshot.notes).map(([key, value]) => (
                      <ListItem key={key}>
                        <ListItemText primary={key} secondary={value} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No notes provided.</Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Typography color="text.secondary">Load a snapshot to view appearance and notes.</Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
